import React from 'react';
import { connect } from 'react-redux';
import { generatePath, RouteComponentProps } from 'react-router';

import { AsyncResourceHandler } from 'src/modules/app';
import { notificationService } from 'src/modules/offer/service';
import { MapDispatch, MapState } from 'src/store';

import { wrapperCustomBackgroundStyles } from '../styles';
import { PageWrapper } from '../../components/PageWrapper';
import { OffersListLayout } from '../../components/OffersListLayout';
import { fetchOfferDetails } from '../../service/offerServices';
import { getOffersList, getOffersListStatus } from '../../store/selectors/offerSelectors';

import { createOfferPdf } from 'src/modules/offerPdf/services/createOfferPdf';
import { OfferPreviewModal } from '../../components/OfferPreviewModal';

import { paths } from 'src/constants/variables';
import { withRouter } from 'react-router-dom';
import { EditType } from '../DuplicateOffer/DuplicateOfferContainer';
import { fetchOffersListForAllUsers } from '../../store/offerStore';
import { ConfirmationDialog } from '../../../../components/ConfirmationDialog';

type StateProps = {
    offers: OfferSummary[];
    status: {
        error: boolean;
        loading: boolean;
    };
};

type DispatchProps = {
    fetchOffers: typeof fetchOffersListForAllUsers;
};

type Props = StateProps & DispatchProps & RouteComponentProps;
type State = {
    offerPreviewSummary?: OfferPreviewSummary;
    isExpiredConfirmationOpen: boolean;
    isDiscontinuedConfirmationOpen: boolean;
    selectedOfferId: number;
    discontinuedArticleIdsForSelectedOffer: string;
    expiredArticleIdsForSelectedOffer: string;
};

const DISCONTINUED_ARTICLE_DIALOG_TITLE = 'Ausgelaufener Artikel';
const EXPIRED_ARTICLE_DIALOG_TITLE = 'Nicht verfügbarer Artikel';
const YES_TEXT = 'Ja';
const CANCEL_TEXT = 'Abbrechen';

class SavedOffers extends React.PureComponent<Props, State> {
    state = {
        offerPreviewSummary: undefined,
        isExpiredConfirmationOpen: false,
        isDiscontinuedConfirmationOpen: false,
        selectedOfferId: -1,
        discontinuedArticleIdsForSelectedOffer: '',
        expiredArticleIdsForSelectedOffer: '',
    };

    componentDidMount() {
        this.props.fetchOffers();
    }

    duplicateOfferAction = async (offerId: OfferSummary['id']) => {
        try {
            this.setState({ selectedOfferId: offerId });
            const { offerItems } = await fetchOfferDetails(offerId);
            const discontinuedItemsIds: string[] = [];
            const expiredItemIds: string[] = [];
            offerItems.forEach((it) => {
                if (it.discontinued) {
                    discontinuedItemsIds.push(it.variantKey);
                }
                if (it.expired) {
                    expiredItemIds.push(it.variantKey);
                }
            });

            if (expiredItemIds.length > 0) {
                this.setState({ isExpiredConfirmationOpen: true });
                this.setState({
                    expiredArticleIdsForSelectedOffer: expiredItemIds.join(', '),
                });
            } else if (discontinuedItemsIds.length > 0) {
                this.setState({ isDiscontinuedConfirmationOpen: true });
                this.setState({
                    discontinuedArticleIdsForSelectedOffer: discontinuedItemsIds.join(', '),
                });
            } else {
                this.confirmedDuplication();
            }
        } catch (err) {
            notificationService.error();
            return false;
        }
    };

    confirmedDuplication = () => {
        const path = generatePath(paths.duplicateOffer, {
            id: this.state.selectedOfferId,
            type: EditType.duplicate,
        });
        this.props.history.push(path);
    };

    getDiscontinuedDialogText = () => {
        return `Der/die Artikel ${this.state.discontinuedArticleIdsForSelectedOffer} ist/sind nicht mehr verfügbar und wird/werden bei der Duplizierung entfernt.`;
    };

    getExpiredDialogText = () => {
        return `Der/die Artikel ${this.state.expiredArticleIdsForSelectedOffer} ist/sind nicht mehr verfügbar und wird/werden bei der Vervielfältigung entfernt.`;
    };

    editOffer = (offerId: OfferSummary['id']) => {
        const path = generatePath(paths.duplicateOffer, { id: offerId, type: EditType.update });
        this.props.history.push(path);
    };

    render() {
        const { offers, status } = this.props;
        const { offerPreviewSummary } = this.state;

        return (
            <PageWrapper contentCss={wrapperCustomBackgroundStyles}>
                {this.state.isExpiredConfirmationOpen && (
                    <ConfirmationDialog
                        title={EXPIRED_ARTICLE_DIALOG_TITLE}
                        text={this.getExpiredDialogText()}
                        acceptButtonLabel={YES_TEXT}
                        acceptButtonAction={() => this.confirmedDuplication()}
                        cancelButtonLabel={CANCEL_TEXT}
                        cancelButtonAction={() => this.setState({
                            isDiscontinuedConfirmationOpen: false,
                            isExpiredConfirmationOpen: false,
                        })}
                    />
                )}

                {this.state.isDiscontinuedConfirmationOpen && (
                    <ConfirmationDialog
                        title={DISCONTINUED_ARTICLE_DIALOG_TITLE}
                        text={this.getDiscontinuedDialogText()}
                        acceptButtonLabel={YES_TEXT}
                        acceptButtonAction={() => this.confirmedDuplication()}
                        cancelButtonLabel={CANCEL_TEXT}
                        cancelButtonAction={() => this.setState({ isDiscontinuedConfirmationOpen: false })}
                    />
                )}

                <AsyncResourceHandler data={{ offers }} {...status}>
                    {(resources) => (
                        <OffersListLayout
                            {...resources}
                            offerActions={{
                                preview: this.previewOffer,
                                duplicate: this.duplicateOfferAction,
                                getPdf: this.downloadOfferPdf,
                                edit: this.editOffer,
                            }}
                        />
                    )}
                </AsyncResourceHandler>
                <OfferPreviewModal
                    offerSummary={offerPreviewSummary}
                    onClick={this.closeOfferPreview}
                />
            </PageWrapper>
        );
    }

    private previewOffer = (offerPreviewSummary: OfferPreviewSummary) => {
        this.setState({ offerPreviewSummary });
    };

    private closeOfferPreview = () => {
        this.setState({ offerPreviewSummary: undefined });
    };

    private downloadOfferPdf = async (offerId: OfferSummary['id']) => {
        try {
            const { offerItems, offerBasics, offerDetails } = await fetchOfferDetails(offerId);
            await createOfferPdf({
                items: offerItems,
                basics: offerBasics,
                details: offerDetails,
                displayCategory: false,
            });
        } catch (err) {
            notificationService.error();
            return false;
        }

        return true;
    };
}

const mapStateToProps: MapState<StateProps> = (state) => ({
    offers: getOffersList(state),
    status: getOffersListStatus(state),
});

const mapDispatchToProps: MapDispatch<DispatchProps> = (dispatch) => ({
    fetchOffers: () => dispatch(fetchOffersListForAllUsers()),
});

export const SavedOffersContainer = withRouter(
    connect(mapStateToProps, mapDispatchToProps)(SavedOffers),
);
